import React, { ReactNode } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import { SxProps } from '@mui/system';
import { Formik, FormikBag, FormikConfig, FormikHelpers, FormikProps, FormikValues } from 'formik';
import { getSnapshot } from 'mobx-state-tree';

import FormAlert from '@common/components/Form/FormAlert';
import { enqueueSnackbar } from '@common/components/SnackBar';

import { Actions, FormWrap } from './styled';

export interface FormValues extends FormikValues {}

export interface FormProps extends Omit<FormikConfig<FormikValues>, 'initialValues' | 'onSubmit'> {
	initialValues?: FormikValues;
	model?: any; // @TODO: TS
	loading?: boolean;
	hideActions?: boolean;
	onSave?: ((data: FormikValues) => void) | ((data: FormikValues) => Promise<any>);
	onSubmit?: (
		values: FormikValues,
		formikBag: FormikBag<FormikProps<FormikValues>, FormikValues>,
	) => void | Promise<any>;
	children?: ReactNode | ((props: FormikProps<FormikValues>) => ReactNode);
	isDefaultGapEnabled?: boolean;
	isMaxWidthEnabled?: boolean;
	withoutSnackbar?: boolean;
	hideErrors?: boolean;
	actionButtonText?: string;
	formActionsStyles?: SxProps;
}

function Form({
	model,
	initialValues,
	loading,
	hideActions,
	onSave,
	children, // @TODO: children as render prop. If needed ask Andriy
	onSubmit,
	isDefaultGapEnabled = false,
	isMaxWidthEnabled = false,
	withoutSnackbar = false,
	hideErrors = false,
	formActionsStyles = {},
	actionButtonText = undefined,
	...props
}: FormProps) {
	const handleSubmit = async (data: FormikValues, { setErrors }: FormikHelpers<any>) => {
		try {
			await onSave(data);
			if (!withoutSnackbar) {
				enqueueSnackbar('Success', {
					variant: 'success',
				});
			}
		} catch (e) {
			setErrors(e.errors);
			if (!withoutSnackbar) {
				enqueueSnackbar('Error', {
					variant: 'error',
				});
			}
			throw e;
		}
	};

	return (
		<Formik
			{...props}
			initialValues={model ? getSnapshot(model) : initialValues}
			onSubmit={onSave ? handleSubmit : onSubmit}
		>
			{(props) => (
				<FormWrap isDefaultGapEnabled={isDefaultGapEnabled} isMaxWidthEnabled={isMaxWidthEnabled}>
					{typeof children === 'function' ? children(props) : children}
					{!hideErrors && <FormAlert />}
					{!hideActions && (
						<Actions sx={formActionsStyles}>
							<LoadingButton loading={loading} type="submit" variant="contained">
								{actionButtonText || 'Save'}
							</LoadingButton>
						</Actions>
					)}
				</FormWrap>
			)}
		</Formik>
	);
}

export default Form;
